/**
 * Logística → Asignaciones (`/logistica/asignaciones`).
 *
 * Puerto de `Shift::schedule` + `views/menu/shift/schedule.php` + el *bundle*
 * `shift.schedule.bundle.js` y su endpoint (`API/Shift` + `Shift_model`).
 *
 * La pantalla reparte los surtidores entre los griferos: se elige la semana y,
 * por cada día que queda por delante, se listan los turnos con un selector por
 * surtidor. En el panel anterior **cada cambio de selector se guardaba solo**, y
 * eso es lo que se mantiene: al cambiar una persona se manda el turno completo de
 * ese día (el servidor guarda el reparto entero).
 */

import { ArrowLeft, CalendarDays, Check, RefreshCw, ShieldAlert, UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { useAsignaciones, useGuardarAsignacion } from '../asignaciones.api';
import type { AsignacionPayload } from '../asignaciones.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Reparto local: clave `día|turno` → usuario por surtidor. */
type Reparto = Record<string, number[]>;

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

export function AsignacionesPage() {
  useDocumentTitle('Logística · Asignaciones');

  const [semana, setSemana] = useState<string>('');
  const [reparto, setReparto] = useState<Reparto>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const asignaciones = useAsignaciones(semana || undefined);
  const guardar = useGuardarAsignacion();

  const sinPermiso = asignaciones.error instanceof ApiError && asignaciones.error.status === 403;
  const datos = asignaciones.data;

  // Al llegar (o cambiar) la semana se reconstruye el reparto local.
  const firma = `${datos?.semana ?? ''}#${datos?.asignaciones.length ?? 0}#${datos?.surtidores ?? 0}`;

  useEffect(() => {
    if (!datos) return;

    const siguiente: Reparto = {};

    for (const item of datos.asignaciones) {
      const valores = new Array<number>(datos.surtidores).fill(0);

      for (const asignado of item.surtidores) {
        if (asignado.surtidor >= 1 && asignado.surtidor <= datos.surtidores) {
          valores[asignado.surtidor - 1] = asignado.usuario;
        }
      }

      siguiente[`${item.dia}|${item.turno_id}`] = valores;
    }

    setReparto(siguiente);
  }, [firma, datos]);

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <UserCog className="size-6 text-brand-500" aria-hidden="true" />
          Asignación de surtidores al personal
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Qué grifero atiende cada surtidor en cada turno de la semana. Los cambios se guardan solos.
        </p>
      </div>

      {guardar.isPending ? (
        <Badge variant="brand">
          <RefreshCw className="size-3 animate-spin" aria-hidden="true" />
          Guardando…
        </Badge>
      ) : (
        <Badge variant="success">
          <Check className="size-3" aria-hidden="true" />
          Al día
        </Badge>
      )}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>logistics</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  /** Cambia un surtidor y guarda el turno completo, como el panel anterior. */
  function cambiar(dia: string, turnoId: number, surtidor: number, usuario: number) {
    if (!datos) return;

    const clave = `${dia}|${turnoId}`;
    const actuales = reparto[clave] ?? new Array<number>(datos.surtidores).fill(0);
    const siguiente = [...actuales];

    siguiente[surtidor - 1] = usuario;
    setReparto({ ...reparto, [clave]: siguiente });
    setError(null);

    const payload: AsignacionPayload = {
      dia,
      turno: turnoId,
      surtidores: siguiente.map((valor, indice) => ({ surtidor: indice + 1, usuario: valor })),
    };

    guardar.mutate(payload, {
      onSuccess: (respuesta) => setAviso(respuesta.mensaje),
      onError: (fallo) => setError(mensajeDe(fallo, 'La API no respondió.')),
    });
  }

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? <Alert variant="success">{aviso}</Alert> : null}
      {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="asignaciones-semana" className="text-sm font-medium text-ink-700">
              Semana
            </label>
            <Select
              id="asignaciones-semana"
              className={cn(CONTROL, 'w-56')}
              value={semana || (datos?.semana ?? '')}
              onChange={(evento) => {
                setSemana(evento.target.value);
                setAviso(null);
                setError(null);
              }}
            >
              {(datos?.semanas ?? []).map((item) => (
                <option key={item.valor} value={item.valor}>
                  {item.etiqueta}
                  {item.valor === datos?.semana_actual ? ' · actual' : ''}
                </option>
              ))}
            </Select>
          </div>

          {datos ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{datos.surtidores} surtidores</Badge>
              <Badge variant="outline">{datos.personal.length} griferos</Badge>
              <Badge variant="outline">{datos.turnos.length} turnos</Badge>
            </div>
          ) : null}

          <span className="ml-auto flex items-center gap-1 text-xs text-ink-400">
            <CalendarDays className="size-3" aria-hidden="true" />
            Sólo se muestran los días que quedan por delante
          </span>
        </CardContent>
      </Card>

      {asignaciones.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : asignaciones.error ? (
        <Alert variant="error" title="No se pudieron leer las asignaciones">
          {mensajeDe(asignaciones.error, 'La API no respondió.')}
        </Alert>
      ) : !datos || datos.dias.length === 0 ? (
        <Alert variant="info" title="Sin días por asignar">
          La semana seleccionada ya pasó. Elige una semana en curso o futura.
        </Alert>
      ) : datos.turnos.length === 0 ? (
        <Alert variant="warning" title="Sin turnos">
          El catálogo de turnos está vacío. Configúralo en Configuración → Turnos.
        </Alert>
      ) : datos.personal.length === 0 ? (
        <Alert variant="warning" title="Sin griferos activos">
          No hay usuarios activos en el grupo de griferos para asignar.
        </Alert>
      ) : (
        <div className="flex flex-col gap-4">
          {datos.dias.map((dia) => (
            <Card key={dia.codigo}>
              <CardContent className="flex flex-col gap-4">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                  <CalendarDays className="size-4 text-brand-500" aria-hidden="true" />
                  <span className="capitalize">{dia.etiqueta}</span>
                  <span className="text-[11px] font-normal text-ink-400">{dia.codigo}</span>
                </h2>

                <div className="grid gap-4 xl:grid-cols-3">
                  {datos.turnos.map((turno) => {
                    const clave = `${dia.codigo}|${turno.id}`;
                    const valores =
                      reparto[clave] ?? new Array<number>(datos.surtidores).fill(0);

                    return (
                      <div
                        key={turno.id}
                        className="flex flex-col gap-2 rounded-card border border-ink-100 bg-ink-50/40 p-3"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-ink-800">{turno.nombre}</span>
                          <span className="text-[11px] text-ink-400">{turno.horario}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          {Array.from({ length: datos.surtidores }, (_, indice) => indice + 1).map(
                            (surtidor) => (
                              <label
                                key={surtidor}
                                className="flex items-center gap-2 text-xs text-ink-500"
                              >
                                <span className="w-16 shrink-0">Surtidor {surtidor}</span>
                                <Select
                                  aria-label={`Surtidor ${surtidor} del turno ${turno.nombre} (${dia.etiqueta})`}
                                  className={CONTROL}
                                  value={String(valores[surtidor - 1] ?? 0)}
                                  onChange={(evento) =>
                                    cambiar(dia.codigo, turno.id, surtidor, Number(evento.target.value))
                                  }
                                >
                                  <option value="0">Sin asignar</option>
                                  {datos.personal.map((persona) => (
                                    <option key={persona.id} value={persona.id}>
                                      {persona.nombre}
                                    </option>
                                  ))}
                                </Select>
                              </label>
                            ),
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
